import { FC } from 'react'
import moment from 'moment'
import { Skeleton } from 'src/components/Skeleton'
import { AnalyticsFetchDailyTrendsResponse } from 'src/services/__generated__/api'
import styles from './topDayCard.module.scss'

// .dayName is min-height 32 inside a 140px .box, and .mentions is 14px
// (topDayCard.module.scss). Mirrors one .box of the card.
const BoxSkeleton: FC = () => (
  <>
    <Skeleton height={32} width={140} />
    <Skeleton height={14} width={72} />
  </>
)

interface TopDayCardProps {
  dailyTrends?: AnalyticsFetchDailyTrendsResponse | null
  hasData?: boolean
  isLoading?: boolean
  mostNegativeDay?: TopDay
  mostPositiveDay?: TopDay
  // Unit shown after the count. Defaults to "Mention(s)"; inbox pages pass "Chat(s)".
  unitLabel?: string
}

export type TopDay = { label: string; total: number } | null

// Picks the real date with the most positive (and the most negative) mentions
// from the daily-trends series. A day with zero of that sentiment is not
// eligible, so an absent sentiment shows "No Data" instead of a bogus date —
// and dates only ever come from the data, never reconstructed/extrapolated.
const pickTopDays = (
  dailyTrends?: AnalyticsFetchDailyTrendsResponse | null
): { mostNegative: TopDay; mostPositive: TopDay } => {
  const dates = dailyTrends?.dates ?? []
  const positives = dailyTrends?.positives ?? []
  const negatives = dailyTrends?.negatives ?? []

  let mostPositive: TopDay = null
  let mostNegative: TopDay = null

  dates.forEach((date, index) => {
    const positive = positives[index] ?? 0
    const negative = negatives[index] ?? 0
    const label = moment(date).format('MMM D, ddd')

    if (positive > 0 && (!mostPositive || positive > mostPositive.total)) {
      mostPositive = { label, total: positive }
    }
    if (negative > 0 && (!mostNegative || negative > mostNegative.total)) {
      mostNegative = { label, total: negative }
    }
  })

  return { mostNegative, mostPositive }
}

export const TopDayCard: FC<TopDayCardProps> = ({
  dailyTrends,
  hasData = false,
  isLoading = false,
  mostNegativeDay,
  mostPositiveDay,
  unitLabel = 'Mention',
}) => {
  const singularUnit = unitLabel.replace(/s$/i, '')
  const pluralUnit = singularUnit.endsWith('s')
    ? singularUnit
    : `${singularUnit}s`
  // total can be absent when the caller hands us a partial payload (e.g. an
  // API response predating the count field); render 0 rather than crash.
  const count = (n?: number) => n ?? 0
  const pickedDays = pickTopDays(dailyTrends)
  const mostPositive = mostPositiveDay ?? pickedDays.mostPositive
  const mostNegative = mostNegativeDay ?? pickedDays.mostNegative

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        {/* Calendar icon */}
        <svg
          className={styles.icon}
          fill="none"
          height="16"
          viewBox="0 0 24 24"
          width="16"
        >
          <rect
            height="16"
            rx="3"
            stroke="#25262D"
            strokeOpacity="0.8"
            strokeWidth="1.5"
            width="18"
            x="3"
            y="4"
          />
          <path
            d="M3 9h18M8 2v4M16 2v4"
            stroke="#25262D"
            strokeLinecap="round"
            strokeOpacity="0.8"
            strokeWidth="1.5"
          />
        </svg>
        <h3 className={styles.title}>Top Day</h3>
      </div>

      <div className={styles.container}>
        {/* Most Positive */}
        <div className={styles.box}>
          <div className={styles.label}>
            <span className={styles.dot} />
            <span className={styles.labelText}>Most Positive</span>
          </div>
          {/* Loading first: dailyTrends is absent while in flight, so the old
              code showed "No Data" for a day it had not looked for yet. */}
          {isLoading ? (
            <BoxSkeleton />
          ) : hasData && mostPositive ? (
            <>
              <div className={styles.dayName}>{mostPositive.label}</div>
              <div className={styles.mentions}>
                {count(mostPositive.total).toLocaleString()}{' '}
                {count(mostPositive.total) === 1 ? singularUnit : pluralUnit}
              </div>
            </>
          ) : (
            <div className={styles.noData}>No Data</div>
          )}
        </div>

        <div className={styles.divider} />

        {/* Most Negative */}
        <div className={styles.box}>
          <div className={styles.label}>
            <span className={styles.dotNegative} />
            <span className={styles.labelText}>Most Negative</span>
          </div>
          {isLoading ? (
            <BoxSkeleton />
          ) : hasData && mostNegative ? (
            <>
              <div className={styles.dayName}>{mostNegative.label}</div>
              <div className={styles.mentions}>
                {count(mostNegative.total).toLocaleString()}{' '}
                {count(mostNegative.total) === 1 ? singularUnit : pluralUnit}
              </div>
            </>
          ) : (
            <div className={styles.noData}>No Data</div>
          )}
        </div>
      </div>
    </div>
  )
}
